import { RECURRENCY_MVP_SETTINGS } from '@cannect/services/resources/recurrency/constants'
import type { RecurrencySettings } from '@cannect/services/resources/recurrency/types'

type Settings = Pick<
  RecurrencySettings,
  'noticeDaysBeforeCharge' | 'editLockDaysBeforeCharge' | 'maxPostponeDays' | 'maxPauseDays' | 'deliveryDays'
>

const MVP: Settings = RECURRENCY_MVP_SETTINGS

// Textos fixos da Compra Recorrente (mockup "Página de produto · opção de
// assinatura" e spec 007). Números vêm por parâmetro quando o serviço informa.
export const recurrencyCopy = {
  badge: {
    available: 'Disponível para Compra Recorrente',
    unavailable: 'Temporariamente indisponível para assinatura'
  },
  purchase: {
    title: 'Como você quer comprar?',
    onceTitle: 'Compra única',
    onceDescription: 'Pague só este pedido',
    subscriptionTitle: 'Assinatura: receba automaticamente',
    subscriptionDescription: (percent: number) => `${percent}% a mais sobre o preço promocional, em todo ciclo`,
    discountChip: (percent: number) => `+${percent}% OFF`,
    unavailable: 'Temporariamente indisponível para assinatura',
    cycleLabel: 'Receber a cada',
    cycleOption: (days: number) => `${days} dias`,
    cycleHintIntense: 'uso intenso',
    cycleHintPopular: 'mais escolhido',
    cycleHintSuggested: (volume?: string | null) => (volume ? `sugerido para ${volume}` : 'sugerido para você'),
    bullets: (s: Settings = MVP) => [
      `Avisamos ${s.noticeDaysBeforeCharge} dias antes de cada cobrança`,
      `Pule, adie, altere ou cancele até ${s.editLockDaysBeforeCharge} dias antes, sem multa`,
      // "Estoque reservado para assinantes" saiu: não há reserva implementada (T081)
      'Lembrete quando sua receita estiver perto de vencer'
    ],
    footer: (deliveryDays = MVP.deliveryDays, { documentsPending = false }: { documentsPending?: boolean } = {}) =>
      `Frete calculado no checkout, sem desconto. Entrega em até ${deliveryDays} dias após cada cobrança. ${
        documentsPending
          ? 'Você pode enviar a receita e a autorização Anvisa depois do pagamento.'
          : 'Requer receita e autorização Anvisa válidas.'
      }`,
    cta: (days: number) => `Assinar: receber a cada ${days} dias`,
    productsTotal: 'Total dos produtos:',
    nextCharge: 'Próxima cobrança:',
    tierChip: (tierPercent: number, subscriberPercent: number) =>
      tierPercent > 0 ? `${tierPercent}% OFF + ${subscriberPercent}%` : `+${subscriberPercent}%`
  },
  infoBox: {
    title: 'Assinatura disponível para este produto.',
    body: 'Como a importação leva cerca de 30 dias, a assinatura garante que o próximo frasco chegue antes do atual acabar.'
  },
  howItWorks: {
    title: 'Como funciona a assinatura',
    steps: (s: Settings = MVP) => [
      {
        title: 'Escolha o intervalo',
        body: '60, 90 ou 120 dias, de acordo com quanto tempo seu produto dura.'
      },
      {
        title: `Aviso ${s.noticeDaysBeforeCharge} dias antes`,
        body: `Você recebe itens, valor e data por e-mail e WhatsApp e pode ajustar até ${s.editLockDaysBeforeCharge} dias antes.`
      },
      {
        title: 'Cobrança e importação',
        body: `Conferimos sua receita, cobramos no dia e o produto chega em até ${s.deliveryDays} dias.`
      },
      {
        title: 'Acompanhamento do acolhimento',
        body: 'Nosso time cuida da sua assinatura e avisa quando a receita precisar ser renovada.'
      }
    ]
  },
  cart: {
    toggle: 'Receber automaticamente',
    toggleDescription: 'Um ciclo para o pedido todo, com desconto de assinante',
    cycleLabel: 'Receber a cada',
    subscriberDiscount: 'Desconto de assinante',
    shipping: 'Frete',
    shippingFree: 'Grátis',
    shippingQuoted: 'Calculado perto de cada cobrança',
    nextCharge: 'Próxima cobrança',
    delivery: (days: number) => `Entrega em até ${days} dias após cada cobrança`,
    flexibility: (s: Settings = MVP) =>
      `Você pode editar, pular, adiar, pausar ou cancelar até ${s.editLockDaysBeforeCharge} dias antes de cada cobrança. Sem fidelidade.`,
    ineligibleTitle: 'Estes itens não entram na Compra Recorrente:',
    sendPrescription: 'Enviar receita',
    schedule: 'Agendar consulta',
    anvisa: 'Enviar autorização Anvisa',
    couponRemoved: 'Cupom não pode ser aplicado em pedido de recorrência',
    documentsPending: (label: string) =>
      `Depois do pagamento, envie ${label}. Sua assinatura fica ativa e os próximos envios dependem da aprovação dos documentos pelo acolhimento.`
  },
  payment: {
    onlyCredit: 'Compra Recorrente só com cartão de crédito à vista',
    terms: (version: string) => `Li e aceito o Termo de uso da Compra Recorrente (v. ${version})`,
    termsLink: 'Ver termo'
  },
  adhesion: {
    created: (date: string, days: number) => `Assinatura criada: próxima cobrança em ${date}, a cada ${days} dias`,
    createdPendingUpload:
      'Assinatura criada · aguardando seus documentos. Se ainda não enviou, envie abaixo para garantir os próximos envios.',
    createdPendingOrder: 'Assinatura criada · aguardando a aprovação dos seus documentos.',
    sendDocuments: 'Enviar documentos',
    pending: 'Estamos criando sua assinatura; ela aparece em Minhas assinaturas em instantes',
    pendingWithoutPage: 'Estamos criando sua assinatura; isso leva só alguns instantes',
    link: 'Ver Minhas assinaturas'
  },
  cards: {
    notReleased: 'Use este cartão numa compra para liberá-lo na assinatura',
    expired: 'Cartão vencido',
    addNew: 'Cadastrar novo cartão',
    empty: 'Nenhum cartão salvo'
  },
  subscriptions: {
    empty: 'Nenhuma assinatura por aqui ainda. Se você acabou de assinar, ela aparece em instantes.',
    cycleLocked: 'Este ciclo já está em processamento; alterações valem para o próximo'
  }
}
